import { useCallback } from 'react';
import { BIO_MAX_LENGTH, DISPLAY_NAME_MAX_LENGTH, STORY_MAX_LENGTH } from '@arthelper/shared/profile-fields';
import { useMutation } from '@apollo/client';
import { GENERATE_CONTENT } from '../graphql/mutations';
import {
  DISPLAY_NAME_PROMPT,
  BIO_PROMPT,
  STORY_PROMPT,
  CATEGORIES_PROMPT,
  ARTIST_STATEMENT_PROMPT,
} from '@arthelper/shared/profile-prompts';

// Returned functions are memoized so callers can safely list them in
// `useEffect` deps without re-firing on every render.
export function useContentGeneration() {
  const [generateContent] = useMutation(GENERATE_CONTENT);

  const generate = useCallback(
    async ({
      prompt,
      context,
      suggestion,
      previousContent,
      maxLength,
      model,
      temperature,
      returnType,
    }: {
      prompt: string;
      context?: any;
      suggestion?: string;
      previousContent?: string;
      maxLength?: number;
      model?: string;
      temperature?: number;
      returnType?: 'TEXT' | 'JSON_ARRAY';
    }): Promise<{ content: string; jsonArray?: string[] }> => {
      const { data } = await generateContent({
        variables: {
          input: {
            prompt,
            context,
            ...(suggestion ? { suggestion } : {}),
            ...(previousContent ? { previousContent } : {}),
            ...(maxLength ? { maxLength } : {}),
            ...(model ? { model } : {}),
            ...(temperature !== undefined ? { temperature } : {}),
            ...(returnType ? { returnType } : {}),
          },
        },
      });
      return {
        content: data.generateContent.content,
        jsonArray: data.generateContent.jsonArray || undefined,
      };
    },
    [generateContent]
  );

  return { generate };
}

// Profile-specific helper hook that uses server-side context building.
// The server will automatically build user context if context is not provided.
export function useProfileGeneration() {
  const { generate } = useContentGeneration();

  const generateDisplayName = useCallback(
    async (suggestion?: string, previousContent?: string): Promise<string> => {
      const result = await generate({
        prompt: DISPLAY_NAME_PROMPT(),
        suggestion,
        previousContent,
        maxLength: DISPLAY_NAME_MAX_LENGTH,
      });
      return result.content;
    },
    [generate]
  );

  const generateBio = useCallback(
    async (suggestion?: string, previousContent?: string): Promise<string> => {
      const result = await generate({
        prompt: BIO_PROMPT(),
        suggestion,
        previousContent,
        maxLength: BIO_MAX_LENGTH,
      });
      return result.content;
    },
    [generate]
  );

  const generateStory = useCallback(
    async (suggestion?: string, previousContent?: string): Promise<string> => {
      const result = await generate({
        prompt: STORY_PROMPT(),
        suggestion,
        previousContent,
        maxLength: STORY_MAX_LENGTH,
      });
      return result.content;
    },
    [generate]
  );

  const generateCategories = useCallback(
    async (suggestion?: string, previousContent?: string): Promise<string[]> => {
      const result = await generate({
        prompt: CATEGORIES_PROMPT,
        suggestion,
        previousContent,
        returnType: 'JSON_ARRAY',
      });
      return result.jsonArray || [];
    },
    [generate]
  );

  const generateArtistStatement = useCallback(
    async (suggestion?: string, previousContent?: string): Promise<string> => {
      const result = await generate({
        prompt: ARTIST_STATEMENT_PROMPT(),
        suggestion,
        previousContent,
        maxLength: 5000,
      });
      return result.content;
    },
    [generate]
  );

  return {
    generateDisplayName,
    generateBio,
    generateStory,
    generateCategories,
    generateArtistStatement,
  };
}
